import { useMemo } from 'react'
import type * as Y from 'yjs'
import { yTextToSlateElement } from '@slate-yjs/core'
import type { TBElement } from '@ttab/textbit'
import type { YDocument } from '@/modules/yjs/hooks'
import { useYValue } from '@/modules/yjs/hooks'

/**
 * Extract the heading-1 text from a list of Slate/Textbit nodes. Mirrors the
 * `makeTitle` logic in `shared/transformations/yjsNewsDoc.ts` so this reflects
 * exactly what ends up as the published document title.
 *
 * The text is returned untrimmed on purpose: `makeTitle` uses `newHeading ||
 * title` with no trimming, so a whitespace-only heading is truthy and gets
 * published as-is. Callers must not trim this value, or they will disagree with
 * what is actually published.
 */
function getHeadingText(children: TBElement[]): string {
  const heading = children.find((child) => {
    return 'properties' in child && child?.properties?.role === 'heading-1'
  })

  const firstChild = heading?.children?.[0]
  if (firstChild && 'text' in firstChild && typeof firstChild.text === 'string') {
    return firstChild.text
  }

  return ''
}

/**
 * Returns the current editable headline (the heading-1 node) of a text document
 * such as a core/article. The headline lives in the document `content`, which is
 * a single `Y.XmlText`, so it can't be read with a plain `useYValue` JSON path -
 * it has to be converted to Slate nodes first (as `makeTitle` does at publish).
 *
 * Returns an empty string when there is no ydoc, no content, or an empty heading.
 */
export function useArticleHeadline(ydoc: YDocument<Y.Map<unknown>> | undefined): string {
  const [content] = useYValue<Y.XmlText>(ydoc?.ele, 'content', true)

  // useYValue returns a stable reference for a raw Y.XmlText, so memoizing on
  // `content` collapses the (whole-body) deserialization to one conversion per
  // content change instead of re-running it on every unrelated re-render.
  return useMemo(() => {
    if (!content || !content.length) {
      return ''
    }

    const children = yTextToSlateElement(content).children as TBElement[]
    return getHeadingText(children)
  }, [content])
}
